/**
 * Seitenkopf nach dem Abschnitt PageHeader des Entwurfs.
 *
 * Brotkrümelpfad und Titel in einer Zeile, daneben das Info-Symbol mit der
 * Erklärung, rechts die Aktionen der Route. Darunter optional eine Chipzeile
 * und der Ladebalken.
 *
 * Der Kopf klebt unter der Kopfzeile (56 px), damit er beim Blättern in langen
 * Tabellen stehen bleibt.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { stil } from './stil';
import { Erklaerung } from './Erklaerung';
import { useToast } from './Toast';
import { useDrawer } from './Drawer';
import { useSeitenaktionen } from './Seitenaktion';
import type { Kopfbereich } from '../entwurf/kopfbereich';

export interface Chip {
  label: string;
  /** Farbtoken für den Punkt; ohne Angabe entfällt er. */
  farbe?: string | null;
}

export interface SeitenkopfProps {
  kopf: Kopfbereich;
  pfad: string;
  /** Chipzeile, wie sie das Rollendetail unter dem Titel zeigt. */
  chips?: Chip[];
  /** Läuft, solange nachgeladen wird. */
  laedt?: boolean;
  /** Erklärungen im Konto-Menü ganz abgeschaltet. */
  erklaerungenAus?: boolean;
}

export function Seitenkopf({ kopf, pfad, chips, laedt, erklaerungenAus }: SeitenkopfProps) {
  const navigate = useNavigate();
  const melden = useToast();
  const drawer = useDrawer();
  const seitenaktionen = useSeitenaktionen();

  const aktion = (label: string, ziel?: string) => {
    /* Erst die Seite fragen: was „Exportieren" hier bedeutet, weiß nur sie.
       Siehe `bausteine/Seitenaktion.tsx` — der Kopf kennt weder die Kacheln
       des Cockpits noch die geladenen Zahlen. */
    if (!ziel && seitenaktionen.ausloesen(label)) return;
    if (!ziel) {
      melden(label, 'Diese Aktion wird im Ausbau der Anwendung angebunden.');
      return;
    }
    if (ziel.startsWith('form:')) drawer.oeffnen(ziel.slice(5));
    else navigate(ziel);
  };

  return (
    <div style={stil(
      'position:sticky;top:56px;z-index:var(--z-seitenkopf);background:var(--surface-page);' +
      'border-bottom:1px solid var(--border-subtle)',
    )}>
      <div style={stil(
        'width:100%;padding:9px 24px 10px;display:flex;align-items:center;gap:20px;flex-wrap:nowrap',
      )}>
        <div style={stil('min-width:0;flex:1')}>
          <nav aria-label="Brotkrümelpfad" style={stil(
            'display:flex;align-items:center;gap:6px;font-size:12px;line-height:15px;color:var(--text-tertiary)',
          )}>
            {kopf.breadcrumb.map((b) => (
              <span key={b.href + b.label} style={stil('display:flex;align-items:center;gap:6px')}>
                <a href={b.href} style={stil('color:var(--text-tertiary)')}>{b.label}</a>
                <span aria-hidden="true">›</span>
              </span>
            ))}
            <span style={stil('color:var(--text-secondary)')}>{kopf.kurz}</span>
          </nav>

          <div style={stil(
            'display:flex;align-items:baseline;gap:10px;flex-wrap:nowrap;min-width:0;margin-top:1px',
          )}>
            <h1 style={stil(
              'margin:0;flex:0 0 auto;font-size:18px;line-height:24px;font-weight:600;letter-spacing:-.01em',
            )}>{kopf.titel}</h1>
            <Erklaerung
              pfad={pfad}
              beschreibung={kopf.beschreibung}
              ausgeschaltet={erklaerungenAus ?? false}
            />
          </div>

          {!!chips?.length && (
            <div style={stil('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px')}>
              {chips.map((c) => (
                <span key={c.label} style={stil(
                  'display:inline-flex;align-items:center;gap:6px;padding:3px 10px;border-radius:999px;' +
                  'font-size:12px;background:var(--surface-card);border:1px solid var(--border-subtle);' +
                  'color:var(--text-secondary)',
                )}>
                  <span style={stil(c.farbe
                    ? 'width:8px;height:8px;border-radius:999px;background:var(' + c.farbe + ')'
                    : 'display:none')} />
                  {c.label}
                </span>
              ))}
            </div>
          )}
        </div>

        <div style={stil(
          'display:flex;align-items:center;justify-content:flex-end;gap:8px;flex:0 0 auto;margin-left:auto',
        )}>
          {kopf.aktionen.map(([label, art, ziel, eintraege]) => (
            eintraege?.length
              ? <Aufklappaktion key={label} label={label} art={art}
                                eintraege={eintraege} waehlen={aktion} />
              : (
                <button key={label} onClick={() => aktion(label, ziel)} style={stil(knopfStil(art))}>
                  {label}
                </button>
              )
          ))}
        </div>
      </div>

      {laedt && (
        <div style={stil('height:2px;overflow:hidden;background:var(--border-subtle)')}>
          <div className="ladebalken" style={stil('height:2px;width:40%;background:var(--brand-primary)')} />
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------- Aufklappaktion --- */

const knopfStil = (art: 'prim' | 'sek') => (art === 'prim'
  ? 'height:32px;padding:0 14px;background:var(--brand-strong);color:var(--text-on-brand);' +
    'border:none;border-radius:var(--r-md);font-size:13px;font-weight:500;cursor:pointer;white-space:nowrap'
  : 'height:32px;padding:0 12px;background:var(--surface-card);color:var(--text-primary);' +
    'border:1px solid var(--border-default);border-radius:var(--r-md);font-size:13px;' +
    'cursor:pointer;white-space:nowrap');

/**
 * Eine Kopfschaltfläche mit Aufklappmenü.
 *
 * Für Handlungen, die dieselbe Sache in verschiedenen Ausführungen tun — auf
 * dem Cockpit „Exportieren" mit CSV und HTML. Zwei gleichrangige Knöpfe
 * nebeneinander behaupten zwei Handlungen, wo es eine ist.
 *
 * Bedienung: Klick oder Eingabetaste öffnet, Pfeiltasten wandern, Escape
 * schließt und gibt den Fokus zurück, ein Klick daneben schließt ebenfalls.
 * Der Knopf trägt `aria-haspopup="menu"` und `aria-expanded`; das Menü ist
 * ein `role="menu"` mit `role="menuitem"` je Eintrag — ohne die Rollen ist es
 * für eine Sprachausgabe eine Ansammlung von Schaltflächen ohne Zusammenhang.
 *
 * Die Einträge sind `<button>`: die Regel für grobe Zeiger in `basis.css`
 * hebt sie damit auf 44 px, ohne dass hier eine Zahl steht. Eine inline
 * gesetzte Höhe unter 44 px schlüge sie aus — dieser Fallstrick hat das
 * Projekt am 13.08.2026 dreimal an einem Tag getroffen.
 */
function Aufklappaktion({ label, art, eintraege, waehlen }: {
  label: string;
  art: 'prim' | 'sek';
  eintraege: string[];
  waehlen: (label: string) => void;
}) {
  const [offen, setzeOffen] = useState(false);
  const huelle = useRef<HTMLDivElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const menue = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offen) return;
    const draussen = (e: MouseEvent) => {
      if (!huelle.current?.contains(e.target as Node)) setzeOffen(false);
    };
    const taste = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setzeOffen(false);
      knopf.current?.focus();
    };
    document.addEventListener('mousedown', draussen);
    document.addEventListener('keydown', taste);
    return () => {
      document.removeEventListener('mousedown', draussen);
      document.removeEventListener('keydown', taste);
    };
  }, [offen]);

  /* Der Fokus wandert in den ersten Eintrag, sobald das Menü steht. Ohne das
     bliebe er am Knopf, und die Pfeiltasten hätten nichts, worauf sie
     wirken. */
  useEffect(() => {
    if (!offen) return;
    menue.current?.querySelector('button')?.focus();
  }, [offen]);

  const wandern = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const knoepfe = [...(menue.current?.querySelectorAll('button') ?? [])];
    const jetzt = knoepfe.indexOf(document.activeElement as HTMLButtonElement);
    const schritt = e.key === 'ArrowDown' ? 1 : -1;
    /* Umlaufend: von unten wieder nach oben. Bei zwei Einträgen ist das der
       Unterschied zwischen „bedienbar" und „hängt am Rand fest". */
    const ziel = knoepfe[(jetzt + schritt + knoepfe.length) % knoepfe.length];
    ziel?.focus();
  };

  return (
    <div ref={huelle} style={stil('position:relative;flex:0 0 auto')}>
      <button
        ref={knopf}
        onClick={() => setzeOffen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={offen}
        style={stil(knopfStil(art) + ';display:inline-flex;align-items:center;gap:6px')}
      >
        {label}
        <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
             style={stil('transition:transform var(--dauer-kurz) ease' +
               (offen ? ';transform:rotate(180deg)' : ''))}>
          <path d="M5 9l7 7 7-7" />
        </svg>
      </button>
      {offen && (
        <div
          ref={menue}
          role="menu"
          aria-label={label}
          onKeyDown={wandern}
          style={stil(
            'position:absolute;top:38px;right:0;z-index:var(--z-menue);min-width:180px;padding:4px;' +
            'background:var(--surface-card);border:1px solid var(--border-default);' +
            'border-radius:var(--r-md);box-shadow:0 10px 28px rgb(0 0 0/.14);' +
            'display:flex;flex-direction:column;gap:2px',
          )}
        >
          {eintraege.map((e) => (
            <button
              key={e}
              role="menuitem"
              onClick={() => { setzeOffen(false); waehlen(e); }}
              style={stil(
                'padding:8px 10px;background:none;border:none;border-radius:var(--r-sm);' +
                'font-size:13px;line-height:18px;text-align:left;color:var(--text-primary);' +
                'cursor:pointer;white-space:nowrap',
              )}
            >
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- Skelette */

/** Platzhalterzeilen, solange die erste Antwort aussteht. */
export function Skelette({ anzahl = 8 }: { anzahl?: number }) {
  return (
    <div aria-busy="true" aria-label="Daten werden geladen"
         style={stil('display:flex;flex-direction:column;gap:12px;padding:16px 24px')}>
      {Array.from({ length: anzahl }, (_, i) => (
        <div key={i} className="skelett" style={stil(
          'height:' + (i === 0 ? '40px' : '32px') + ';border-radius:var(--r-md);' +
          'background:var(--surface-sunken)',
        )} />
      ))}
    </div>
  );
}
